import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { Pencil } from 'lucide-react';
import { NavigateFunction } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { MonthDay } from '@/modules/common/types/month-day';
import { SettingsRoutes } from '@/router/routes';
import { formatShortDate } from '@/utils/date-formatters';

import { ApprovalRequiredCell } from '../components/time-offs/approval-required-cell';
import { ScheduledChangeBadge } from '../components/time-offs/scheduled-change-badge';
import { TIME_OFF_FORMAT_LABEL_KEY } from '../constants/time-offs.constants';
import { TimeOffTypeListItem } from '../types/time-off';

interface GetTimeOffTypesColumnsParams {
  t: TFunction;
  navigate: NavigateFunction;
}

export const getTimeOffTypesColumns = ({
  t,
  navigate,
}: GetTimeOffTypesColumnsParams): ColumnDef<TimeOffTypeListItem>[] => [
  {
    id: 'type',
    accessorKey: 'code',
    size: 240,
    header: () => <div className="min-w-[240px]">{t('pages.timeOff.columns.type')}</div>,
    cell: ({ row }) => {
      const { displayName, scheduledChange } = row.original;
      return (
        <div className="flex min-w-[240px] items-center gap-2">
          <span>{displayName}</span>
          {scheduledChange && <ScheduledChangeBadge scheduledChange={scheduledChange} />}
        </div>
      );
    },
  },
  {
    id: 'format',
    accessorKey: 'format',
    size: 140,
    header: () => <div className="min-w-[140px]">{t('pages.timeOff.columns.format')}</div>,
    cell: ({ row }) => {
      const format = row.original.format;
      return (
        <div className="min-w-[140px]">
          {format ? t(TIME_OFF_FORMAT_LABEL_KEY[format]) : t('common:table.noValue')}
        </div>
      );
    },
  },
  {
    id: 'days',
    accessorKey: 'allocationDays',
    size: 120,
    header: () => <div className="min-w-[120px]">{t('pages.timeOff.columns.days')}</div>,
    cell: ({ row }) => {
      const allocationDays = row.original.allocationDays;
      return (
        <div className="min-w-[120px]">
          {allocationDays == null
            ? t('common:table.noValue')
            : t('pages.timeOff.daysValue', { count: allocationDays })}
        </div>
      );
    },
  },
  {
    id: 'dateOfRenewal',
    accessorKey: 'dateOfRenewal',
    size: 160,
    header: () => <div className="min-w-[160px]">{t('pages.timeOff.columns.dateOfRenewal')}</div>,
    cell: ({ row }) => {
      if (!row.original.dateOfRenewal) {
        return <div className="min-w-[160px]">{t('common:table.noValue')}</div>;
      }

      const { month, day } = row.original.dateOfRenewal as MonthDay;
      const date = new Date(2024, month - 1, day);
      return (
        <div className="min-w-[160px]">
          {`${formatShortDate(date)} ${t('common:monthDayPicker.eachYearSuffix')}`}
        </div>
      );
    },
  },
  {
    id: 'isApprovalRequired',
    accessorKey: 'isApprovalRequired',
    size: 160,
    header: () => (
      <div className="min-w-[160px]">{t('pages.timeOff.columns.approvalRequired')}</div>
    ),
    cell: ({ row }) => (
      <div className="min-w-[160px]">
        {row.original.isApprovalRequired == null ? (
          t('common:table.noValue')
        ) : (
          <ApprovalRequiredCell isApprovalRequired={row.original.isApprovalRequired} />
        )}
      </div>
    ),
  },
  {
    id: 'actions',
    size: 88,
    header: () => (
      <div className="flex w-full justify-end">{t('pages.timeOff.columns.action')}</div>
    ),
    cell: ({ row }) => (
      <div className="flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={event => {
            event.stopPropagation();
            navigate(SettingsRoutes.TimeOffDetails.replace(':typeId', String(row.original.id)));
          }}
        >
          <Pencil className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
    ),
  },
];
